<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  createContactSearcher,
  createNewContact,
  fetchContactableInboxes,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import ConversationApi from 'dashboard/api/inbox/conversation';
import SearchAPI from 'dashboard/api/search';
import { createPendingMessage } from 'dashboard/helper/commons';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';

// Forward a message's text + attachments into another conversation. The target
// is picked from the same conversation search the sidebar uses; the payload is
// re-sent via the normal outgoing path so it goes out on that conversation's
// channel (a real WhatsApp send).
//
// Both the recent-chat quick list and the typed search only offer targets the
// SENDING NUMBER can see: forwarding from a WhatsApp chat offers that one line's
// chats, and a marketplace/email source (which has no number of its own) falls
// back to its company's WhatsApp lines. A group is a separate conversation per
// line that is in it, so an unscoped list showed identical-looking rows for
// numbers you weren't sending from.
//
// MakerHub: you can also forward to a phone number that has no existing
// conversation — we resolve/create the contact and create a fresh WhatsApp
// conversation.
// Accepts a single `message` (right-click one message → Forward) or a `messages`
// array (multi-select → Forward). Each selected message is forwarded as its own
// outgoing message, in original order, so it matches WhatsApp-style forwarding.
const props = defineProps({
  message: { type: Object, default: null },
  messages: { type: Array, default: () => [] },
});
// `forwarded` fires with the ids that actually went out (not on cancel), so the
// caller can drop only those from a multi-select and keep any failures selected
// for a retry.
const emit = defineEmits(['close', 'forwarded']);

// Ordered list of messages to forward (single-message prop normalised to one).
const messageList = computed(() => {
  if (props.messages?.length) return props.messages;
  return props.message ? [props.message] : [];
});
const isMulti = computed(() => messageList.value.length > 1);

const store = useStore();
const { t } = useI18n();

const searchContacts = createContactSearcher();

const query = ref('');
const isForwarding = ref(false);
const selectedId = ref(null);

// Phone-number forward state.
const isResolvingPhone = ref(false);
const pendingContact = ref(null);
const channelChoices = ref([]);
// contactId -> conversationId created this session, so a retry after a partial
// failure reuses the conversation instead of creating a duplicate + resending.
const createdConversationByContact = ref(new Map());

// Recent-chat quick list. Starts loading — we always fetch on mount, so the
// spinner (not the empty state) shows on first paint.
const recentChats = ref([]);
const isLoadingRecent = ref(true);

// Target search results are LOCAL, not the shared `conversationSearch` store
// slice. That slice is append-only (it powers SearchView/ChatList infinite
// scroll) and is rendered live by the inbox rail, which is mounted at the same
// time as this dialog — sharing it meant every keystroke stacked another copy
// of the same conversations here, and clearing it would wipe the rail's search.
const results = ref([]);
const isSearching = ref(false);

const currentUser = useMapGetter('getCurrentUser');
const currentChat = useMapGetter('getSelectedChat');
const inboxById = useMapGetter('inboxes/getInboxById');
const allInboxes = useMapGetter('inboxes/getInboxes');

// The store inbox carries the uploaded channel logo, but `getInboxById`
// camelCases it (avatarUrl). Show that logo — the real channel WhatsApp/Shopee/
// Lazada, which is also our outgoing channel for the chat — and fall back to a
// WhatsApp mark (every target here is a WhatsApp channel).
const inboxAvatar = id => inboxById.value(id)?.avatarUrl || '';

// The inbox the forwarded message came from (the open conversation). We default
// the new-number send to this channel and derive the company from it.
const sourceInboxId = computed(
  () => Number(currentChat.value?.inbox_id) || null
);

// This deployment's WhatsApp channels run through Evolution as Channel::Api
// inboxes (the native Channel::Whatsapp inbox is unused), and api_contactable_inbox
// returns EVERY Api inbox for any contact — including Shopee/Lazada/TikTok, which
// can't be messaged by phone. The only field distinguishing the WhatsApp ones is
// the WS-<number>-<company> naming convention, so match that (plus any genuine
// Channel::Whatsapp inbox). Reads both camel (contactable inboxes) and snake
// (store records) shapes.
const inboxName = i => i?.name || '';
const inboxChannel = i => i?.channelType || i?.channel_type;
const isWhatsappInbox = i =>
  inboxChannel(i) === INBOX_TYPES.WHATSAPP ||
  (inboxChannel(i) === INBOX_TYPES.API && /^WS[\s-]/i.test(inboxName(i)));

// Company by inbox-name suffix — same convention as the store panel / ChatList
// (-S3 = Smith3D, everything else = MakerHub).
const companyOf = i => (/[-\s]S3$/i.test(inboxName(i).trim()) ? 's3' : 'mk');
const sourceCompany = computed(() =>
  companyOf(inboxById.value(sourceInboxId.value))
);
const isSameCompanyWhatsapp = i =>
  isWhatsappInbox(i) && companyOf(i) === sourceCompany.value;

// The source company's WhatsApp channels (MakerHub is a mix of two numbers).
const companyWhatsappInboxes = computed(() =>
  (allInboxes.value || []).filter(isSameCompanyWhatsapp)
);

// Which numbers' chats may be offered as forward targets. A WhatsApp group or
// contact exists as a SEPARATE conversation per line that is in it, so offering
// another number's copy shows an identical-looking row you cannot actually send
// from. Forwarding from a WhatsApp chat therefore only offers that one number.
// Marketplace/email sources have no number of their own, so they fall back to
// the company's WhatsApp lines (keeps Shopee → staff-group forwarding working).
const targetInboxes = computed(() => {
  const sourceInbox = inboxById.value(sourceInboxId.value);
  return isWhatsappInbox(sourceInbox)
    ? [sourceInbox]
    : companyWhatsappInboxes.value;
});
const targetInboxIds = computed(() =>
  targetInboxes.value.map(inbox => Number(inbox.id))
);

const trimmedQuery = computed(() => query.value.trim());
const showingRecent = computed(() => trimmedQuery.value.length < 2);

// Empty query → the recent quick list; once you type, the search hits. Both are
// already limited to `targetInboxes` (recent by which inboxes it fetches, search
// by the `inbox_ids` param), so neither can offer another number's chats.
const targetList = computed(() =>
  showingRecent.value ? recentChats.value : results.value
);

// Only the newest query may write `results` — a slow earlier response must not
// repopulate the list with stale targets after you have retyped.
let latestSearchToken = 0;

const runSearch = debounce(async q => {
  const term = (q || '').trim();
  if (term.length < 2) return;
  latestSearchToken += 1;
  const token = latestSearchToken;
  isSearching.value = true;
  try {
    const { data } = await SearchAPI.conversations({
      q: term,
      page: 1,
      inboxIds: targetInboxIds.value.join(','),
    });
    if (token !== latestSearchToken) return;
    results.value = data?.payload?.conversations || [];
  } catch (error) {
    if (token === latestSearchToken) results.value = [];
  } finally {
    if (token === latestSearchToken) isSearching.value = false;
  }
}, 400);

watch(query, q => {
  selectedId.value = null;
  channelChoices.value = [];
  pendingContact.value = null;
  // Drop the previous query's hits immediately (and cancel any in-flight
  // response) so shortening the query can never leave unrelated targets on
  // screen while the next search debounces.
  latestSearchToken += 1;
  results.value = [];
  isSearching.value = q.trim().length >= 2;
  runSearch(q);
});

// Treat a query that is mostly digits (optionally +/space/dash) as a phone
// number worth offering a "new chat" forward for.
const isPhoneQuery = computed(() => {
  const q = trimmedQuery.value;
  return /^\+?[\d\s-]{7,}$/.test(q) && q.replace(/\D/g, '').length >= 7;
});

const contentOf = message => message.content ?? message.contentContent ?? '';

// Evolution stamps incoming GROUP messages with a leading `**<phone> - <name>:**`
// line (chatwoot.service.ts group path) so the timeline shows which participant
// spoke. That stamp is ingest metadata, not message text — a forward must not
// leak the original sender's contact to the recipient (native WhatsApp forwards
// carry only the content), so strip it before re-sending.
const GROUP_SENDER_STAMP_RE = /^\*\*[^\n]+ - [^\n]+:\*\*(?:\n\n|$)/;
const forwardContentOf = message =>
  contentOf(message).replace(GROUP_SENDER_STAMP_RE, '');

// Build the `files` payload for a forwarded message. Prefer the existing blob's
// signed_id (MessageBuilder reattaches it with zero re-download/-upload); fall
// back to re-materialising from the stored URL only when no signed_id exists
// (e.g. external/Instagram attachments).
const attachmentsForForward = async message => {
  const files = await Promise.all(
    (message.attachments || []).map(async att => {
      const signedId = att.signed_id || att.signedId;
      if (signedId) return signedId;

      const url = att.data_url || att.dataUrl;
      if (!url) return null;
      const response = await fetch(url);
      // fetch() does NOT reject on 404/403 — without this the error-page body
      // would be uploaded to the customer as a corrupt file. Throwing makes the
      // message count as a (retryable) failed forward instead.
      if (!response.ok) {
        throw new Error(`Attachment fetch failed: ${response.status}`);
      }
      const blob = await response.blob();
      const name = url.split('/').pop()?.split('?')[0] || 'attachment';
      return new File([blob], name, { type: blob.type });
    })
  );
  return files.filter(Boolean);
};

// Send one forwarded message into an existing conversation and WAIT for the
// real API call to finish. `createPendingMessageAndSend` is fire-and-forget
// (it does not await the send), which would race a multi-message loop out of
// order — dispatching `sendMessageWithData` directly lets us await each send
// and surface send errors.
const sendForwardedMessage = async (conversationId, message) => {
  const files = await attachmentsForForward(message);
  await store.dispatch(
    'sendMessageWithData',
    createPendingMessage({
      conversationId,
      message: forwardContentOf(message) || '',
      files,
      private: false,
      // Tag it so the sent message renders the WhatsApp-style "Forwarded" label
      // (same content_attributes.forwarded flag Evolution sets on inbound forwards).
      contentAttributes: { forwarded: true },
    })
  );
};

// Best-effort + ordered: attempt EACH message once, in order. A failure does
// not abort the batch (later messages aren't skipped). Returns the ids that
// actually went out; the caller keeps the rest selected for a retry.
const forwardMessagesInOrder = async (conversationId, messages) => {
  const sentIds = [];
  await messages.reduce(
    (chain, message) =>
      chain.then(async () => {
        try {
          await sendForwardedMessage(conversationId, message);
          sentIds.push(message.id);
        } catch (error) {
          // Leave it unsent — it stays selected so the dialog can retry it.
        }
      }),
    Promise.resolve()
  );
  return sentIds;
};

// Report the batch outcome: all sent, all failed, or partial (X of Y).
const reportForwardOutcome = (sent, total) => {
  if (sent === total) {
    useAlert(
      total > 1
        ? t('CONVERSATION.CONTEXT_MENU.FORWARD.SUCCESS_MULTI', { count: total })
        : t('CONVERSATION.CONTEXT_MENU.FORWARD.SUCCESS')
    );
  } else if (sent === 0) {
    useAlert(t('CONVERSATION.CONTEXT_MENU.FORWARD.ERROR'));
  } else {
    useAlert(t('CONVERSATION.CONTEXT_MENU.FORWARD.PARTIAL', { sent, total }));
  }
};

// Recent WhatsApp chats of the source company, newest first, one row per
// contact — a one-click forward target without searching.
const loadRecentChats = async () => {
  isLoadingRecent.value = true;
  try {
    const lists = await Promise.all(
      targetInboxes.value.map(inbox =>
        ConversationApi.get({
          inboxId: inbox.id,
          status: 'all',
          assigneeType: 'all',
          sortBy: 'last_activity_at_desc',
          page: 1,
        })
          .then(r => r.data?.data?.payload || [])
          .catch(() => [])
      )
    );
    const byContact = new Map();
    lists
      .flat()
      .sort((a, b) => (b.last_activity_at || 0) - (a.last_activity_at || 0))
      .forEach(conv => {
        const contact = conv.meta?.sender;
        if (contact?.id && !byContact.has(contact.id)) {
          byContact.set(contact.id, {
            id: conv.id,
            contact,
            inbox: inboxById.value(conv.inbox_id),
          });
        }
      });
    recentChats.value = [...byContact.values()].slice(0, 10);
  } finally {
    isLoadingRecent.value = false;
  }
};

onMounted(loadRecentChats);

const forwardTo = async conversationId => {
  if (isForwarding.value) return;
  isForwarding.value = true;
  selectedId.value = conversationId;
  // Snapshot the batch: `forwarded` prunes the parent selection, which shrinks
  // props.messages, so we must not re-read messageList after emitting.
  const batch = messageList.value;
  try {
    const sentIds = await forwardMessagesInOrder(conversationId, batch);
    reportForwardOutcome(sentIds.length, batch.length);
    // Tell the caller exactly which ids went out so it drops only those and
    // keeps any failures selected. Close only on full success — a partial
    // failure leaves the dialog open to retry just the remaining (now-fewer)
    // messages against another target.
    if (sentIds.length) emit('forwarded', sentIds);
    if (sentIds.length === batch.length) emit('close');
  } finally {
    isForwarding.value = false;
    selectedId.value = null;
  }
};

// Resolve an existing contact for the typed number (reuse it if we've chatted
// before), otherwise create one, so we don't spawn duplicate contacts.
const resolveContactForPhone = async () => {
  const q = trimmedQuery.value;
  const digits = q.replace(/\D/g, '');
  let matches = [];
  try {
    matches = (await searchContacts(q, { skipMinLength: true })) || [];
  } catch (error) {
    matches = [];
  }
  const existing = matches.find(
    c => String(c.phoneNumber || '').replace(/\D/g, '') === digits
  );
  if (existing) return existing;
  const e164 = q.startsWith('+') ? q : `+${digits}`;
  return createNewContact(e164);
};

// Create a brand-new WhatsApp conversation for `inbox` and send the forwarded
// text + attachments as its first message (isFromWhatsApp:false = the FormData
// path that carries attachments, same as a normal compose send).
const forwardToNumber = async inbox => {
  if (isForwarding.value) return;
  const contactId = Number(pendingContact.value?.id);
  // Retry after a partial failure: the conversation for THIS number on THIS
  // channel already exists, so route through the existing-conversation path
  // instead of creating a duplicate. Keyed by contact + inbox so a retry that
  // deliberately picks a different WhatsApp channel still opens its own thread.
  const cacheKey = `${contactId}:${inbox.id}`;
  const existingId =
    contactId && createdConversationByContact.value.get(cacheKey);
  if (existingId) {
    await forwardTo(existingId);
    return;
  }
  isForwarding.value = true;
  const batch = messageList.value;
  const [firstMessage, ...restMessages] = batch;
  try {
    // The first message creates the fresh WhatsApp conversation (create throws
    // on failure → caught below, nothing sent, selection kept).
    const firstFiles = await attachmentsForForward(firstMessage);
    const conversation = await store.dispatch('contactConversations/create', {
      params: {
        inboxId: inbox.id,
        sourceId: inbox.sourceId,
        contactId,
        message: {
          content: forwardContentOf(firstMessage) || '',
          content_attributes: { forwarded: true },
        },
        assigneeId: currentUser.value?.id,
        files: firstFiles,
      },
      isFromWhatsApp: false,
    });
    createdConversationByContact.value.set(cacheKey, conversation.id);
    // First message is committed; best-effort the rest into the new conversation.
    const sentIds = [
      firstMessage.id,
      ...(await forwardMessagesInOrder(conversation.id, restMessages)),
    ];
    reportForwardOutcome(sentIds.length, batch.length);
    emit('forwarded', sentIds);
    if (sentIds.length === batch.length) emit('close');
  } catch (error) {
    // Conversation creation (or the first attachment) failed → nothing was
    // sent; keep the selection so a retry is safe (no duplicate conversation).
    useAlert(t('CONVERSATION.CONTEXT_MENU.FORWARD.ERROR'));
  } finally {
    isForwarding.value = false;
    channelChoices.value = [];
    pendingContact.value = null;
  }
};

const startPhoneForward = async () => {
  if (isForwarding.value || isResolvingPhone.value) return;
  isResolvingPhone.value = true;
  try {
    const contact = await resolveContactForPhone();
    // Same number scoping as the target list: a forward must go out on a line
    // the source conversation is allowed to send from, so keep only the
    // contactable WhatsApp channels that are in `targetInboxes`.
    const inboxes = (await fetchContactableInboxes(contact.id)).filter(
      inbox =>
        isWhatsappInbox(inbox) &&
        targetInboxIds.value.includes(Number(inbox.id))
    );
    if (!inboxes.length) {
      useAlert(t('CONVERSATION.CONTEXT_MENU.FORWARD.NO_WHATSAPP_INBOX'));
      return;
    }
    pendingContact.value = contact;
    // Always show the channel picker — never auto-send. Forwarding to a new
    // number is a real outgoing WhatsApp message, so the send must be an
    // explicit second click. Default (sort first + mark) the inbox this message
    // came from, but let staff pick another.
    channelChoices.value = [...inboxes].sort((a, b) => {
      if (Number(a.id) === sourceInboxId.value) return -1;
      if (Number(b.id) === sourceInboxId.value) return 1;
      return 0;
    });
  } catch (error) {
    useAlert(t('CONVERSATION.CONTEXT_MENU.FORWARD.ERROR'));
  } finally {
    isResolvingPhone.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full gap-3 p-1">
    <h2 class="text-lg font-medium text-n-slate-12">
      {{
        isMulti
          ? $t('CONVERSATION.CONTEXT_MENU.FORWARD.TITLE_MULTI', {
              count: messageList.length,
            })
          : $t('CONVERSATION.CONTEXT_MENU.FORWARD.TITLE')
      }}
    </h2>
    <input
      v-model="query"
      type="text"
      class="w-full reset-base"
      :placeholder="$t('CONVERSATION.CONTEXT_MENU.FORWARD.SEARCH_PLACEHOLDER')"
    />
    <div class="flex flex-col gap-1 overflow-y-auto max-h-80 min-h-20">
      <!-- Channel picker: number reachable on multiple WhatsApp inboxes -->
      <template v-if="channelChoices.length">
        <p class="px-1 py-1 text-xs text-n-slate-11">
          {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.CHOOSE_CHANNEL') }}
        </p>
        <button
          v-for="inbox in channelChoices"
          :key="inbox.id"
          type="button"
          :disabled="isForwarding"
          class="flex items-center w-full gap-2 p-2 text-left rounded-lg hover:bg-n-alpha-1 disabled:opacity-50"
          @click="forwardToNumber(inbox)"
        >
          <img
            v-if="inboxAvatar(inbox.id)"
            :src="inboxAvatar(inbox.id)"
            :alt="inbox.name"
            class="object-cover rounded-sm size-5 shrink-0"
          />
          <span
            v-else
            class="i-ri-whatsapp-fill size-5 text-[#25D366] shrink-0"
          />
          <span class="text-sm truncate text-n-slate-12">{{ inbox.name }}</span>
          <span
            v-if="Number(inbox.id) === sourceInboxId"
            class="px-1.5 py-0.5 text-xs rounded text-n-slate-11 bg-n-alpha-1"
          >
            {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.DEFAULT_CHANNEL') }}
          </span>
          <Spinner v-if="isForwarding" class="ml-auto" />
        </button>
      </template>
      <template v-else>
        <!-- Forward to a brand-new phone number -->
        <button
          v-if="isPhoneQuery"
          type="button"
          :disabled="isForwarding || isResolvingPhone"
          class="flex items-center w-full gap-2 p-2 text-left rounded-lg hover:bg-n-alpha-1 disabled:opacity-50"
          @click="startPhoneForward"
        >
          <span class="i-ri-whatsapp-fill size-6 text-[#25D366]" />
          <span class="text-sm truncate text-n-slate-12">
            {{
              $t('CONVERSATION.CONTEXT_MENU.FORWARD.NEW_CHAT', {
                number: trimmedQuery,
              })
            }}
          </span>
          <Spinner v-if="isResolvingPhone" class="ml-auto" />
        </button>
        <!-- Recent quick list header -->
        <p
          v-if="showingRecent && targetList.length"
          class="px-1 py-1 text-xs text-n-slate-11"
        >
          {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.RECENT') }}
        </p>
        <div
          v-if="
            (showingRecent && isLoadingRecent) ||
            (!showingRecent && isSearching)
          "
          class="flex items-center justify-center py-6 text-n-slate-11"
        >
          <Spinner />
        </div>
        <p
          v-else-if="showingRecent && !targetList.length"
          class="py-6 text-sm text-center text-n-slate-11"
        >
          {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.NO_RECENT') }}
        </p>
        <p
          v-else-if="!showingRecent && !isPhoneQuery && !targetList.length"
          class="py-6 text-sm text-center text-n-slate-11"
        >
          {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.NO_RESULTS') }}
        </p>
        <button
          v-for="conv in targetList"
          :key="conv.id"
          type="button"
          :disabled="isForwarding"
          class="flex items-center w-full gap-2.5 p-2 text-left rounded-lg hover:bg-n-alpha-1 disabled:opacity-50"
          @click="forwardTo(conv.id)"
        >
          <Avatar
            :name="conv.contact?.name || `#${conv.id}`"
            :src="conv.contact?.thumbnail"
            :size="32"
          />
          <div class="flex flex-col min-w-0 flex-1">
            <span class="text-sm truncate text-n-slate-12">
              {{ conv.contact?.name || `#${conv.id}` }}
              <span v-if="conv.contact?.phone_number" class="text-n-slate-10">
                {{ conv.contact.phone_number }}
              </span>
            </span>
            <span
              class="flex items-center gap-1 text-xs truncate text-n-slate-11"
            >
              <img
                v-if="inboxAvatar(conv.inbox?.id)"
                :src="inboxAvatar(conv.inbox?.id)"
                :alt="conv.inbox?.name"
                class="object-cover rounded-sm size-3.5 shrink-0"
              />
              <!-- Recent rows are all WhatsApp; broad search rows can be any
                   channel, so fall back to the channel-type icon there. -->
              <span
                v-else-if="showingRecent"
                class="i-ri-whatsapp-fill size-3.5 text-[#25D366] shrink-0"
              />
              <ChannelIcon
                v-else-if="conv.inbox"
                :inbox="conv.inbox"
                class="size-3.5 shrink-0"
              />
              <span class="truncate">
                {{
                  conv.inbox?.name || conv.inbox?.channel_type || `#${conv.id}`
                }}
              </span>
            </span>
          </div>
          <Spinner
            v-if="isForwarding && selectedId === conv.id"
            class="ml-auto shrink-0"
          />
        </button>
      </template>
    </div>
    <div class="flex justify-end gap-2">
      <NextButton
        faded
        slate
        :label="$t('CONVERSATION.CONTEXT_MENU.FORWARD.CANCEL')"
        @click="emit('close')"
      />
    </div>
  </div>
</template>
